import * as React from "react";
import { StaticImage } from "gatsby-plugin-image";
import { FormattedMessage, useIntl } from "react-intl";
import ReCAPTCHA from "react-google-recaptcha";
import RichText from "./rich-text";

const formatDateForBrevo = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const ContactText = () => {
  const { locale } = useIntl();

  if (locale === "en") {
    return (
      <p>
        For any information request, we invite you to fill out the form below.
        You can also give us a call at{" "}
        <a href="tel:+33189315582">+33 1 89 31 55 82</a>.
      </p>
    );
  }

  return (
    <p>
      Pour toute demande d'information, nous vous invitons à remplir le
      formulaire ci-dessous ou à nous appeler au{" "}
      <a href="tel:+33189315582">+33 1 89 31 55 82</a>.
    </p>
  );
};

const ContactForm = () => {
  const { formatMessage, locale } = useIntl();
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState("");
  const [utmParams, setUtmParams] = React.useState(null);
  const formEl = React.useRef(null);
  const recaptchaEl = React.useRef(null);
  const newsletter = formatMessage({ id: "contactNewsletter" });
  const confirmation = formatMessage({ id: "contactConfirmation" });

  const handleSubmit = (event) => {
    event.preventDefault();
    recaptchaEl.current.execute();
  };

  const handleChange = async () => {
    setError("");

    const formData = new FormData(formEl.current);
    formData.set("DATE_FORMSPREE", formatDateForBrevo(new Date()));
    const dateFormspree = formData.get("DATE_FORMSPREE");

    const listId = formData.get("listId");
    const locale = formData.get("locale");
    const email = formData.get("email");
    const firstName = formData.get("firstName");
    const lastName = formData.get("lastName");
    const phone = formData.get("phone");
    const newsletter = formData.get("newsletter");
    const message = formData.get("message");
    const utmCampaign = formData.get("utm_campaign") ?? undefined;
    const utmId = formData.get("utm_id") ?? undefined;
    const utmSource = formData.get("utm_source") ?? undefined;
    const utmMedium = formData.get("utm_medium") ?? undefined;
    const utmTerm = formData.get("utm_term") ?? undefined;

    const newsletterResponse = await fetch("/api/newsletter-subscription", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
      },
      body: JSON.stringify({
        listId,
        email,
        firstName,
        lastName,
        phone,
        locale,
        newsletter,
        message,
        utmCampaign,
        utmId,
        utmSource,
        utmMedium,
        utmTerm,
        DATE_FORMSPREE: dateFormspree,
      }),
    });

    if (!newsletterResponse.ok) {
      const data = await newsletterResponse.json();
      setError(data.message);
      recaptchaEl.current.reset();
      return;
    }

    const response = await fetch("https://formspree.io/f/xayavypj", {
      method: "POST",
      body: formData,
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      const data = await response.json();
      setError(`${data.errors[0].field} ${data.errors[0].message}`);
      recaptchaEl.current.reset();
      return;
    }

    setSuccess(true);
    window.scrollTo(0, 0);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "FormSubmitOK",
      userEmail: email,
      userPhone: phone,
    });
  };

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setUtmParams(
        Object.keys(localStorage)
          .filter((key) => key.startsWith("utm_"))
          .map((key) => ({ key, value: localStorage.getItem(key) }))
      );
    }
  }, []);

  return (
    <>
      <RichText>
        {!success && (
          <>
            <ContactText />
            <form ref={formEl} onSubmit={handleSubmit}>
              <input type="hidden" name="listId" value={17} />
              <input type="hidden" name="locale" value={locale} />
              <input
                type="hidden"
                name="DATE_FORMSPREE"
                id="DATE_FORMSPREE"
              />
              {utmParams &&
                utmParams.map(({ key, value }) => (
                  <input type="hidden" key={key} name={key} value={value} />
                ))}
              <label>
                <input
                  type="text"
                  name="lastName"
                  autoComplete="family-name"
                  placeholder={formatMessage({ id: "contactLastName" })}
                  required
                />
                <span>
                  <FormattedMessage id="contactLastName" /> *
                </span>
              </label>
              <label>
                <input
                  type="text"
                  name="firstName"
                  autoComplete="given-name"
                  placeholder={formatMessage({ id: "contactFirstName" })}
                  required
                />
                <span>
                  <FormattedMessage id="contactFirstName" /> *
                </span>
              </label>
              <label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder={formatMessage({ id: "contactEmail" })}
                  required
                />
                <span>
                  <FormattedMessage id="contactEmail" /> *
                </span>
              </label>
              <label>
                <input
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  placeholder={formatMessage({ id: "contactPhone" })}
                  pattern="^\+\d{6,15}$"
                  title="+33600000000"
                />
                <span>
                  <FormattedMessage id="contactPhone" />
                </span>
              </label>
              <label>
                <textarea name="message" placeholder="Message" required />
                <span>Message *</span>
              </label>
              <label>
                <input type="checkbox" name="consent" required />
                <span>
                  <FormattedMessage id="contactConsent" />
                </span>
              </label>
              <label>
                <input type="checkbox" name="newsletter" />
                <span dangerouslySetInnerHTML={{ __html: newsletter }} />
              </label>
              <hr />
              <button
                type="submit"
                className="button-link"
                style={{ margin: 0 }}
              >
                <FormattedMessage id="contactSend" />
              </button>
              <ReCAPTCHA
                ref={recaptchaEl}
                size="invisible"
                sitekey="6LcCWWgcAAAAAMX0Bfo5GDtTRuIyaLjDXcVSaBvX"
                onChange={handleChange}
              />
            </form>
          </>
        )}
        {error && <p>{error}</p>}
        {success && <div dangerouslySetInnerHTML={{ __html: confirmation }} />}
      </RichText>
      {success && (
        <StaticImage src="../assets/mountains-2.jpg" quality={80} alt="" />
      )}
    </>
  );
};

export default ContactForm;
